import { parseSlideBlockGroupId } from '../utils/slideBlockGroups';
import { parseSlideTableBlock } from '../utils/programSlideTableBlock';
import { parseTextRunsFromJson } from '../utils/slideTextRuns';

/** 슬라이드 캔버스 JSON (DB `layout_json`) */
export type ProgramContentSlideOwnerKind = 'program_cell' | 'fixed_row' | 'scoped_row' | 'presentation_ref';
export type ProgramSlideTextFontFamilyV1 =
  | 'pretendard'
  | 'isamanru'
  | 'batang'
  | 'badasseugi'
  | 'bunpil';

export interface ProgramContentSlideOwnerRef {
  kind: ProgramContentSlideOwnerKind;
  id: string;
}

/** 텍스트 박스 내 구간 색. `colorHex` 생략 시 박스 기본 `colorHex`와 동일. */
export interface ProgramSlideTextRunV1 {
  text: string;
  colorHex?: string;
}

export interface ProgramSlideTextBoxV1 {
  id: string;
  /** 캔버스 너비 대비 0~1 */
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  /** 부분 색. 없으면 `text` 전체가 `colorHex`. 있을 때 `text`는 런 조인과 일치해야 함. */
  textRuns?: ProgramSlideTextRunV1[];
  fontSizePx: number;
  fontFamily?: ProgramSlideTextFontFamilyV1;
  colorHex: string;
  bold: boolean;
  textAlign?: 'justify' | 'left' | 'center' | 'right';
  lineHeight?: number;
  zIndex: number;
  /** 함께 선택·이동되는 그룹(슬라이드당 1단, 중첩 없음) */
  groupId?: string;
  /** true면 이동·리사이즈·내용 편집 불가(잠금 토글만 가능) */
  locked?: boolean;
}

export interface ProgramSlideButtonBlockV1 {
  id: string;
  kind: 'link' | 'dismiss';
  /** 캔버스 너비 대비 0~1 */
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  href: string;
  fontSizePx: number;
  fontFamily?: ProgramSlideTextFontFamilyV1;
  colorHex: string;
  backgroundColorHex: string;
  bold: boolean;
  cornerRadiusPx: number;
  zIndex: number;
  groupId?: string;
}

/** 슬라이드 자유 곡선(폴리라인). 좌표는 캔버스 정규화 0~1 */
export interface ProgramSlidePolylineBlockV1 {
  id: string;
  points: Array<{ x: number; y: number }>;
  strokeColorHex: string;
  strokeWidthPx: number;
  /** 시작점 방향 화살표 표시 */
  startArrow?: boolean;
  /** 끝점 방향 화살표 표시 */
  endArrow?: boolean;
  zIndex: number;
  groupId?: string;
  locked?: boolean;
}

/** 슬라이드 도형 블럭 (직사각형·원). bbox 안에 텍스트를 겹쳐 표시한다. */
/** 'ruled' = 글쓰기 블럭(직사각형 테두리 + 가로 줄) */
export type ProgramSlideShapeKindV1 = 'rect' | 'circle' | 'ruled';

export type ProgramSlideRuledBackgroundV1 = 'lines' | 'grid' | 'dots';
export const SLIDE_RULED_DEFAULT_SPACING_PX = 30;
export const SLIDE_RULED_MIN_SPACING_PX = 16;
export const SLIDE_RULED_MAX_SPACING_PX = 80;

export interface ProgramSlideShapeBlockV1 {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  shape: ProgramSlideShapeKindV1;
  /** 도형 채우기 (투명은 빈 문자열) */
  fillColorHex: string;
  strokeColorHex: string;
  strokeWidthPx: number;
  /** `rect`에서만 사용. `circle`은 UI에서 숨김. */
  cornerRadiusPx: number;
  text: string;
  fontSizePx: number;
  fontFamily?: ProgramSlideTextFontFamilyV1;
  colorHex: string;
  bold: boolean;
  textAlign?: 'justify' | 'left' | 'center' | 'right';
  zIndex: number;
  groupId?: string;
  locked?: boolean;
  /** 글쓰기 블럭: 줄 간격(px, A4 캔버스 기준) */
  ruledLineSpacingPx?: number;
  /** 글쓰기 블럭: 줄 색 */
  ruledLineColorHex?: string;
  /** 글쓰기 블럭: 점선 여부 */
  ruledLineDashed?: boolean;
  /** 글쓰기 블럭 배경: 줄(기본) / 격자 선 / 격자 점 */
  ruledBackground?: ProgramSlideRuledBackgroundV1;
}

export interface ProgramSlideImageCropV1 {
  /** 원본 이미지 기준 0~1 */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ProgramSlideImageBlockV1 {
  id: string;
  /** 캔버스 너비 대비 0~1 */
  x: number;
  y: number;
  w: number;
  h: number;
  src: string;
  /** 파일 픽셀 가로 (불러오기 시 고정) */
  naturalW: number;
  naturalH: number;
  /**
   * 가로 방향 표시 비율(1 = 파일 그대로). 크롭·contain 계산의 유효 가로는 naturalW * aspectWMultiplier.
   */
  aspectWMultiplier: number;
  /**
   * true: crop x,y,w,h는 편집 프레임(이미지 몸통) 기준 0~1.
   * false(또는 구 데이터): letterbox 안쪽 정규화 좌표(구 방식).
   */
  cropUsesFrameFrac: boolean;
  crop: ProgramSlideImageCropV1;
  zIndex: number;
  groupId?: string;
  locked?: boolean;
}

/** 클립보드 붙여넣기·Storage URL 전용 이미지 블럭(필드는 URL 이미지 블럭과 동일) */
export type ProgramSlideClipboardImageBlockV1 = ProgramSlideImageBlockV1;

/** 웹캠 촬영·Storage URL 전용 이미지 블럭(필드는 URL 이미지 블럭과 동일) */
export type ProgramSlideScanImageBlockV1 = ProgramSlideImageBlockV1;

export interface ProgramSlideVideoBookmarkV1 {
  id: string;
  label: string;
  timeSec: number;
}

export interface ProgramSlideVideoBlockV1 {
  id: string;
  /** 캔버스 너비 대비 0~1 */
  x: number;
  y: number;
  w: number;
  h: number;
  src: string;
  bookmarks: ProgramSlideVideoBookmarkV1[];
  zIndex: number;
  groupId?: string;
}

export type ProgramSlideWebpageBlockModeV1 = 'url' | 'code';

/** 웹페이지 링크 카드 또는 격리된 소스코드 실행 블럭(iframe sandbox) */
export interface ProgramSlideWebpageBlockV1 {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  mode?: ProgramSlideWebpageBlockModeV1;
  /** mode='url'일 때 사용 */
  url: string;
  /** mode='url'일 때 표시 타이틀 */
  title: string;
  /** mode='url'일 때 썸네일 */
  /** slide-images public URL 또는 저장 전 blob: */
  thumbSrc: string;
  /** 미리보기 API에서 embed 가능할 때만 */
  embedSrc?: string;
  /** mode='code'일 때만 사용 (사용자 입력 HTML/CSS/JS) */
  sourceCode?: string;
  /** mode='code'일 때 iframe·블럭 배경 투명 여부 (기본 true) */
  codeTransparentBackground?: boolean;
  zIndex: number;
  groupId?: string;
}

/** 단답형 퀴즈 빈칸 (보기 모드: Enter로 채점) */
export interface ProgramSlideQuizShortAnswerV1 {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  correctAnswer: string;
  placeholder?: string;
  fontSizePx: number;
  fontFamily?: ProgramSlideTextFontFamilyV1;
  colorHex: string;
  backgroundColorHex: string;
  bold?: boolean;
  cornerRadiusPx: number;
  zIndex: number;
  groupId?: string;
}

/** 단답형 퀴즈 안내문·기본 텍스트 색 */
export const QUIZ_DEFAULT_TEXT_COLOR_HEX = '#84888e';

/** 선택형 퀴즈 세트 메타 (4지선다 1세트) */
export interface ProgramSlideQuizChoiceSetV1 {
  id: string;
  /** 정답 보기 인덱스 (0~7), 복수 가능 */
  correctOptionIndices: number[];
  zIndex: number;
}

/** 선택형 퀴즈 보기 블럭 */
export interface ProgramSlideQuizChoiceOptionV1 {
  id: string;
  setId: string;
  optionIndex: number;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  fontSizePx: number;
  fontFamily?: ProgramSlideTextFontFamilyV1;
  colorHex: string;
  backgroundColorHex: string;
  bold?: boolean;
  cornerRadiusPx: number;
  zIndex: number;
  groupId?: string;
}

export type ProgramSlideTableTextAlignV1 = 'left' | 'center' | 'right';
export type ProgramSlideTableVerticalAlignV1 = 'top' | 'middle' | 'bottom';

export interface ProgramSlideTableCellV1 {
  id: string;
  text: string;
  textAlign: ProgramSlideTableTextAlignV1;
  verticalAlign?: ProgramSlideTableVerticalAlignV1;
  colorHex: string;
  backgroundColorHex: string;
  bold?: boolean;
  rowSpan: number;
  colSpan: number;
}

export interface ProgramSlideTableBlockV1 {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rowCount: number;
  colCount: number;
  cells: ProgramSlideTableCellV1[];
  /** 병합 가림 슬롯: `"row,col"` → 앵커 셀 id */
  coveredBy?: Record<string, string>;
  rowHeightsFrac: number[];
  colWidthsFrac: number[];
  borderColorHex: string;
  borderWidthPx: number;
  defaultFontSizePx: number;
  defaultFontFamily?: ProgramSlideTextFontFamilyV1;
  headerRow?: boolean;
  zIndex: number;
  groupId?: string;
  locked?: boolean;
}

export interface ProgramSlideLayoutV1 {
  v: 1;
  appliedLayoutTemplateId?: string;
  /** 전체 슬라이드 기본 텍스트색(새 개체 생성 기본값) */
  defaultTextColorHex?: string;
  /** 전체 슬라이드 기본 배경색(페이지별 배경색 미지정 시 사용) */
  defaultBackgroundColorHex?: string;
  /** 페이지 개별 배경색 */
  pageBackgroundColorHex?: string;
  /** 페이지 개별 텍스트색(단원명·안내 등 페이지 UI 및 새 개체 기본값) */
  pageTextColorHex?: string;
  boxes: ProgramSlideTextBoxV1[];
  images: ProgramSlideImageBlockV1[];
  clipboardImages: ProgramSlideClipboardImageBlockV1[];
  scanImages: ProgramSlideScanImageBlockV1[];
  buttons: ProgramSlideButtonBlockV1[];
  shapes: ProgramSlideShapeBlockV1[];
  polylines: ProgramSlidePolylineBlockV1[];
  tables?: ProgramSlideTableBlockV1[];
  videos: ProgramSlideVideoBlockV1[];
  webpages?: ProgramSlideWebpageBlockV1[];
  quizzes: ProgramSlideQuizShortAnswerV1[];
  quizChoiceSets: ProgramSlideQuizChoiceSetV1[];
  quizChoiceOptions: ProgramSlideQuizChoiceOptionV1[];
}

export type ProgramSlideLayoutPatch = Omit<
  ProgramSlideLayoutV1,
  'quizChoiceSets' | 'quizChoiceOptions' | 'clipboardImages' | 'scanImages' | 'shapes' | 'polylines' | 'tables'
> & {
  clipboardImages?: ProgramSlideClipboardImageBlockV1[];
  scanImages?: ProgramSlideScanImageBlockV1[];
  quizChoiceSets?: ProgramSlideQuizChoiceSetV1[];
  quizChoiceOptions?: ProgramSlideQuizChoiceOptionV1[];
  shapes?: ProgramSlideShapeBlockV1[];
  polylines?: ProgramSlidePolylineBlockV1[];
  tables?: ProgramSlideTableBlockV1[];
  webpages?: ProgramSlideWebpageBlockV1[];
};

export const EMPTY_SLIDE_LAYOUT: ProgramSlideLayoutV1 = {
  v: 1,
  boxes: [],
  images: [],
  clipboardImages: [],
  scanImages: [],
  buttons: [],
  shapes: [],
  polylines: [],
  tables: [],
  videos: [],
  webpages: [],
  quizzes: [],
  quizChoiceSets: [],
  quizChoiceOptions: [],
};

export function normalizeSlideTextFontFamily(raw: unknown): ProgramSlideTextFontFamilyV1 {
  if (raw === 'batang' || raw === 'isamanru' || raw === 'badasseugi' || raw === 'bunpil') {
    return raw;
  }
  /* 예전 나눔펜 저장값 → 학교안심 받아쓰기 */
  if (raw === 'handwriting') return 'badasseugi';
  return 'pretendard';
}

export const PROGRAM_SLIDE_TEXT_FONT_OPTIONS: Array<{
  value: ProgramSlideTextFontFamilyV1;
  label: string;
  cssFamily: string;
}> = [
  {
    value: 'pretendard',
    label: '프리텐다드',
    cssFamily:
      "'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Apple SD Gothic Neo', sans-serif",
  },
  {
    value: 'isamanru',
    label: '이사만루',
    cssFamily: "'Isamanru', 'Pretendard', 'Apple SD Gothic Neo', sans-serif",
  },
  {
    value: 'batang',
    label: '고운바탕',
    cssFamily: "'Gowun Batang', 'Noto Serif KR', serif",
  },
  {
    value: 'badasseugi',
    label: '학교안심 받아쓰기 체',
    cssFamily: "'Hakgyoansim Badasseugi', 'Apple SD Gothic Neo', cursive",
  },
  {
    value: 'bunpil',
    label: '학교안심 분필체',
    cssFamily: "'Hakgyoansim Bunpil', 'Apple SD Gothic Neo', cursive",
  },
];

export function slideTextFontCssFamily(fontFamily: ProgramSlideTextFontFamilyV1 | undefined): string {
  const normalized = normalizeSlideTextFontFamily(fontFamily);
  return (
    PROGRAM_SLIDE_TEXT_FONT_OPTIONS.find((option) => option.value === normalized)?.cssFamily ??
    PROGRAM_SLIDE_TEXT_FONT_OPTIONS[0].cssFamily
  );
}

function programSlideTextBoxPlainText(b: ProgramSlideTextBoxV1): string {
  if (b.textRuns && b.textRuns.length > 0) {
    return b.textRuns.map((r) => r.text).join('');
  }
  return b.text ?? '';
}

/** 2번째 슬라이드 이후(추가 캔버스, DB `page_index` ≥ 0)에 사용자가 넣은 내용이 있는지 */
export function slideLayoutHasMeaningfulCanvasContent(layout: ProgramSlideLayoutV1 | null | undefined): boolean {
  if (!layout || layout.v !== 1) return false;
  for (const b of layout.boxes ?? []) {
    if (programSlideTextBoxPlainText(b).replace(/\u200b/g, '').trim()) return true;
  }
  for (const im of layout.images ?? []) {
    if ((im.src ?? '').trim()) return true;
  }
  for (const im of layout.clipboardImages ?? []) {
    if ((im.src ?? '').trim()) return true;
  }
  for (const im of layout.scanImages ?? []) {
    if ((im.src ?? '').trim()) return true;
  }
  for (const v of layout.videos ?? []) {
    if ((v.src ?? '').trim()) return true;
  }
  for (const wp of layout.webpages ?? []) {
    if ((wp.url ?? '').trim()) return true;
    if ((wp.thumbSrc ?? '').trim()) return true;
  }
  for (const q of layout.quizzes ?? []) {
    if ((q.correctAnswer ?? '').replace(/\u200b/g, '').trim()) return true;
  }
  for (const set of layout.quizChoiceSets ?? []) {
    if ((set.correctOptionIndices ?? []).length > 0) return true;
  }
  const defaultLinkLabel = '링크 버튼';
  for (const btn of layout.buttons ?? []) {
    const t = (btn.text ?? '').trim();
    if (btn.kind === 'link') {
      if ((btn.href ?? '').trim()) return true;
      if (t && t !== defaultLinkLabel) return true;
    } else if (t && t !== '?') {
      return true;
    }
  }
  for (const sh of layout.shapes ?? []) {
    if ((sh.text ?? '').replace(/\u200b/g, '').trim()) return true;
    const fill = (sh.fillColorHex ?? '').trim();
    if (fill && fill !== 'transparent') return true;
    const sw = Number(sh.strokeWidthPx);
    if (Number.isFinite(sw) && sw > 0) return true;
  }
  for (const pl of layout.polylines ?? []) {
    if (Array.isArray(pl.points) && pl.points.length >= 2) return true;
  }
  for (const tbl of layout.tables ?? []) {
    if ((tbl.cells ?? []).some((c) => (c.text ?? '').trim())) return true;
    if (tbl.rowCount > 0 && tbl.colCount > 0) return true;
  }
  return false;
}

export function parseSlideLayoutJson(raw: unknown): ProgramSlideLayoutV1 {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SLIDE_LAYOUT };
  const o = raw as {
    v?: unknown;
    appliedLayoutTemplateId?: unknown;
    defaultTextColorHex?: unknown;
    defaultBackgroundColorHex?: unknown;
    pageBackgroundColorHex?: unknown;
    pageTextColorHex?: unknown;
    boxes?: unknown;
    images?: unknown;
    clipboardImages?: unknown;
    scanImages?: unknown;
    buttons?: unknown;
    videos?: unknown;
    webpages?: unknown;
    quizzes?: unknown;
    quizChoiceSets?: unknown;
    quizChoiceOptions?: unknown;
    shapes?: unknown;
    polylines?: unknown;
    tables?: unknown;
  };
  if (o.v !== 1 || !Array.isArray(o.boxes)) return { ...EMPTY_SLIDE_LAYOUT };
  const defaultTextColorRaw = String(o.defaultTextColorHex ?? '').trim();
  const defaultTextColorHex = defaultTextColorRaw ? normalizeHexColor(defaultTextColorRaw) : undefined;
  const defaultBackgroundRaw = String(o.defaultBackgroundColorHex ?? '').trim();
  const defaultBackgroundColorHex = defaultBackgroundRaw
    ? normalizeButtonBackgroundHex(defaultBackgroundRaw)
    : undefined;
  const pageBackgroundRaw = String(o.pageBackgroundColorHex ?? '').trim();
  const pageBackgroundColorHex = pageBackgroundRaw ? normalizeButtonBackgroundHex(pageBackgroundRaw) : undefined;
  const pageTextRaw = String(o.pageTextColorHex ?? '').trim();
  const pageTextColorHex = pageTextRaw ? normalizeHexColor(pageTextRaw) : undefined;
  const boxes: ProgramSlideTextBoxV1[] = [];
  for (const b of o.boxes) {
    if (!b || typeof b !== 'object') continue;
    const x = (b as { x?: unknown }).x;
    const y = (b as { y?: unknown }).y;
    const w = (b as { w?: unknown }).w;
    const h = (b as { h?: unknown }).h;
    const text = String((b as { text?: unknown }).text ?? '');
    const colorHex = normalizeHexColor((b as { colorHex?: unknown }).colorHex);
    const textRuns = parseTextRunsFromJson((b as { textRuns?: unknown }).textRuns, colorHex, text);
    const groupId = parseSlideBlockGroupId((b as { groupId?: unknown }).groupId);
    boxes.push({
      id: String((b as { id?: unknown }).id || genSlideBoxId(boxes.length)),
      x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.1,
      y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.1,
      w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.4,
      h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.15,
      text,
      ...(textRuns ? { textRuns } : {}),
      fontSizePx: clampFontSize(Number((b as { fontSizePx?: unknown }).fontSizePx)),
      fontFamily: normalizeSlideTextFontFamily((b as { fontFamily?: unknown }).fontFamily),
      colorHex,
      bold: !!(b as { bold?: unknown }).bold,
      textAlign: normalizeTextAlign((b as { textAlign?: unknown }).textAlign),
      lineHeight: clampLineHeight(Number((b as { lineHeight?: unknown }).lineHeight)),
      zIndex: Number.isFinite(Number((b as { zIndex?: unknown }).zIndex))
        ? Number((b as { zIndex?: unknown }).zIndex)
        : boxes.length,
      ...(groupId ? { groupId } : {}),
      ...((b as { locked?: unknown }).locked ? { locked: true } : {}),
    });
  }
  const images = parseSlideImageBlockArray(o.images, boxes.length, 0);
  const clipboardImages = parseSlideImageBlockArray(
    o.clipboardImages,
    boxes.length,
    images.length
  );
  const scanImages = parseSlideImageBlockArray(
    o.scanImages,
    boxes.length,
    images.length + clipboardImages.length
  );
  const buttons: ProgramSlideButtonBlockV1[] = [];
  if (Array.isArray(o.buttons)) {
    for (const btn of o.buttons) {
      if (!btn || typeof btn !== 'object') continue;
      const x = (btn as { x?: unknown }).x;
      const y = (btn as { y?: unknown }).y;
      const w = (btn as { w?: unknown }).w;
      const h = (btn as { h?: unknown }).h;
      const kind = (btn as { kind?: unknown }).kind === 'dismiss' ? 'dismiss' : 'link';
      const btnGroupId = parseSlideBlockGroupId((btn as { groupId?: unknown }).groupId);
      buttons.push({
        id: String((btn as { id?: unknown }).id || genSlideBoxId(buttons.length)),
        kind,
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.18,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.18,
        w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.22,
        h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.08,
        text: String((btn as { text?: unknown }).text ?? (kind === 'dismiss' ? '?' : '링크 버튼')),
        href: kind === 'link' ? normalizeButtonHref((btn as { href?: unknown }).href) : '',
        fontSizePx: clampCompactFontSize(Number((btn as { fontSizePx?: unknown }).fontSizePx)),
        fontFamily: normalizeSlideTextFontFamily((btn as { fontFamily?: unknown }).fontFamily),
        colorHex: normalizeHexColor((btn as { colorHex?: unknown }).colorHex),
        backgroundColorHex: normalizeButtonBackgroundHex((btn as { backgroundColorHex?: unknown }).backgroundColorHex),
        bold: !!(btn as { bold?: unknown }).bold,
        cornerRadiusPx: clampCornerRadius(Number((btn as { cornerRadiusPx?: unknown }).cornerRadiusPx)),
        zIndex: Number.isFinite(Number((btn as { zIndex?: unknown }).zIndex))
          ? Number((btn as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length,
        ...(btnGroupId ? { groupId: btnGroupId } : {}),
      });
    }
  }
  const videos: ProgramSlideVideoBlockV1[] = [];
  if (Array.isArray(o.videos)) {
    for (const video of o.videos) {
      if (!video || typeof video !== 'object') continue;
      const x = (video as { x?: unknown }).x;
      const y = (video as { y?: unknown }).y;
      const w = (video as { w?: unknown }).w;
      const h = (video as { h?: unknown }).h;
      const videoGroupId = parseSlideBlockGroupId((video as { groupId?: unknown }).groupId);
      videos.push({
        id: String((video as { id?: unknown }).id || genSlideBoxId(videos.length)),
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.16,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.16,
        w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.34,
        h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.34,
        src: normalizeImageSrc((video as { src?: unknown }).src),
        bookmarks: normalizeVideoBookmarks((video as { bookmarks?: unknown }).bookmarks),
        zIndex: Number.isFinite(Number((video as { zIndex?: unknown }).zIndex))
          ? Number((video as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + videos.length,
        ...(videoGroupId ? { groupId: videoGroupId } : {}),
      });
    }
  }
  const webpages: ProgramSlideWebpageBlockV1[] = [];
  if (Array.isArray(o.webpages)) {
    for (const wp of o.webpages) {
      if (!wp || typeof wp !== 'object') continue;
      const x = (wp as { x?: unknown }).x;
      const y = (wp as { y?: unknown }).y;
      const w = (wp as { w?: unknown }).w;
      const h = (wp as { h?: unknown }).h;
      const wpGroupId = parseSlideBlockGroupId((wp as { groupId?: unknown }).groupId);
      const embedRaw = (wp as { embedSrc?: unknown }).embedSrc;
      const embedSrc =
        typeof embedRaw === 'string' && embedRaw.trim() ? normalizeButtonHref(embedRaw) : undefined;
      const modeRaw = (wp as { mode?: unknown }).mode;
      const mode: ProgramSlideWebpageBlockModeV1 = modeRaw === 'code' ? 'code' : 'url';
      const sourceCodeRaw = (wp as { sourceCode?: unknown }).sourceCode;
      const sourceCode =
        typeof sourceCodeRaw === 'string' && sourceCodeRaw.length > 0
          ? sourceCodeRaw.slice(0, 120_000)
          : undefined;
      const codeTransparentRaw = (wp as { codeTransparentBackground?: unknown }).codeTransparentBackground;
      const codeTransparentBackground =
        mode === 'code' && codeTransparentRaw === false ? false : undefined;
      webpages.push({
        id: String((wp as { id?: unknown }).id || genSlideBoxId(webpages.length)),
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.14,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.14,
        w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.36,
        h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.28,
        mode,
        url: normalizeButtonHref((wp as { url?: unknown }).url),
        title: String((wp as { title?: unknown }).title ?? '').slice(0, 200),
        thumbSrc: normalizeImageSrc((wp as { thumbSrc?: unknown }).thumbSrc),
        ...(embedSrc ? { embedSrc } : {}),
        ...(sourceCode ? { sourceCode } : {}),
        ...(codeTransparentBackground === false ? { codeTransparentBackground: false } : {}),
        zIndex: Number.isFinite(Number((wp as { zIndex?: unknown }).zIndex))
          ? Number((wp as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + videos.length + webpages.length,
        ...(wpGroupId ? { groupId: wpGroupId } : {}),
      });
    }
  }
  const quizzes: ProgramSlideQuizShortAnswerV1[] = [];
  if (Array.isArray(o.quizzes)) {
    for (const q of o.quizzes) {
      if (!q || typeof q !== 'object') continue;
      const x = (q as { x?: unknown }).x;
      const y = (q as { y?: unknown }).y;
      const w = (q as { w?: unknown }).w;
      const h = (q as { h?: unknown }).h;
      const quizGroupId = parseSlideBlockGroupId((q as { groupId?: unknown }).groupId);
      quizzes.push({
        id: String((q as { id?: unknown }).id || genSlideBoxId(quizzes.length)),
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.2,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.22,
        w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.36,
        h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.13,
        correctAnswer: String((q as { correctAnswer?: unknown }).correctAnswer ?? ''),
        placeholder: String((q as { placeholder?: unknown }).placeholder ?? '?'),
        fontSizePx: clampFontSize(Number((q as { fontSizePx?: unknown }).fontSizePx)),
        fontFamily: normalizeSlideTextFontFamily((q as { fontFamily?: unknown }).fontFamily),
        colorHex: normalizeQuizTextColorHex((q as { colorHex?: unknown }).colorHex),
        backgroundColorHex: normalizeButtonBackgroundHex((q as { backgroundColorHex?: unknown }).backgroundColorHex),
        bold: Boolean((q as { bold?: unknown }).bold),
        cornerRadiusPx: clampCornerRadius(Number((q as { cornerRadiusPx?: unknown }).cornerRadiusPx)),
        zIndex: Number.isFinite(Number((q as { zIndex?: unknown }).zIndex))
          ? Number((q as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + videos.length + quizzes.length,
        ...(quizGroupId ? { groupId: quizGroupId } : {}),
      });
    }
  }
  const quizChoiceSets: ProgramSlideQuizChoiceSetV1[] = [];
  const quizChoiceOptions: ProgramSlideQuizChoiceOptionV1[] = [];
  if (Array.isArray(o.quizChoiceSets)) {
    for (const s of o.quizChoiceSets) {
      if (!s || typeof s !== 'object') continue;
      const rawIndices = (s as { correctOptionIndices?: unknown }).correctOptionIndices;
      const correctOptionIndices: number[] = [];
      if (Array.isArray(rawIndices)) {
        for (const idx of rawIndices) {
          const n = Number(idx);
          if (Number.isInteger(n) && n >= 0 && n < 8) correctOptionIndices.push(n);
        }
      }
      quizChoiceSets.push({
        id: String((s as { id?: unknown }).id || genSlideBoxId(quizChoiceSets.length)),
        correctOptionIndices: Array.from(new Set(correctOptionIndices)).sort((a, b) => a - b),
        zIndex: Number.isFinite(Number((s as { zIndex?: unknown }).zIndex))
          ? Number((s as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + videos.length + quizzes.length + quizChoiceSets.length,
      });
    }
  }
  if (Array.isArray(o.quizChoiceOptions)) {
    for (const opt of o.quizChoiceOptions) {
      if (!opt || typeof opt !== 'object') continue;
      const x = (opt as { x?: unknown }).x;
      const y = (opt as { y?: unknown }).y;
      const w = (opt as { w?: unknown }).w;
      const h = (opt as { h?: unknown }).h;
      const optionIndex = Number((opt as { optionIndex?: unknown }).optionIndex);
      const optGroupId = parseSlideBlockGroupId((opt as { groupId?: unknown }).groupId);
      quizChoiceOptions.push({
        id: String((opt as { id?: unknown }).id || genSlideBoxId(quizChoiceOptions.length)),
        setId: String((opt as { setId?: unknown }).setId ?? ''),
        optionIndex: Number.isInteger(optionIndex) && optionIndex >= 0 && optionIndex < 8 ? optionIndex : 0,
        label: String((opt as { label?: unknown }).label ?? '1번'),
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.12,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.2,
        w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.12,
        h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.12,
        fontSizePx: clampCompactFontSize(Number((opt as { fontSizePx?: unknown }).fontSizePx)),
        fontFamily: normalizeSlideTextFontFamily((opt as { fontFamily?: unknown }).fontFamily),
        colorHex: normalizeHexColor((opt as { colorHex?: unknown }).colorHex),
        backgroundColorHex: normalizeButtonBackgroundHex((opt as { backgroundColorHex?: unknown }).backgroundColorHex),
        bold: Boolean((opt as { bold?: unknown }).bold),
        cornerRadiusPx: clampCornerRadius(Number((opt as { cornerRadiusPx?: unknown }).cornerRadiusPx)),
        zIndex: Number.isFinite(Number((opt as { zIndex?: unknown }).zIndex))
          ? Number((opt as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + videos.length + quizzes.length + quizChoiceOptions.length,
        ...(optGroupId ? { groupId: optGroupId } : {}),
      });
    }
  }
  const shapes: ProgramSlideShapeBlockV1[] = [];
  if (Array.isArray(o.shapes)) {
    for (const sh of o.shapes) {
      if (!sh || typeof sh !== 'object') continue;
      const x = (sh as { x?: unknown }).x;
      const y = (sh as { y?: unknown }).y;
      const w = (sh as { w?: unknown }).w;
      const h = (sh as { h?: unknown }).h;
      const rawShape = (sh as { shape?: unknown }).shape;
      const shape: ProgramSlideShapeKindV1 = rawShape === 'circle' ? 'circle' : rawShape === 'ruled' ? 'ruled' : 'rect';
      const ruledSp = Number((sh as { ruledLineSpacingPx?: unknown }).ruledLineSpacingPx);
      const ruledColor = (sh as { ruledLineColorHex?: unknown }).ruledLineColorHex;
      const strokeW = Number((sh as { strokeWidthPx?: unknown }).strokeWidthPx);
      const shapeGroupId = parseSlideBlockGroupId((sh as { groupId?: unknown }).groupId);
      shapes.push({
        id: String((sh as { id?: unknown }).id || genSlideBoxId(shapes.length)),
        x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.2,
        y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.22,
        w: typeof w === 'number' && Number.isFinite(w) ? clampShapeDim(w) : 0.28,
        h: typeof h === 'number' && Number.isFinite(h) ? clampShapeDim(h) : 0.14,
        shape,
        fillColorHex: normalizeShapeFillHex((sh as { fillColorHex?: unknown }).fillColorHex),
        strokeColorHex: normalizeHexColor((sh as { strokeColorHex?: unknown }).strokeColorHex),
        strokeWidthPx: Number.isFinite(strokeW) ? Math.min(16, Math.max(0, Math.round(strokeW))) : 2,
        cornerRadiusPx: clampCornerRadius(Number((sh as { cornerRadiusPx?: unknown }).cornerRadiusPx)),
        text: String((sh as { text?: unknown }).text ?? ''),
        fontSizePx: clampCompactFontSize(Number((sh as { fontSizePx?: unknown }).fontSizePx)),
        fontFamily: normalizeSlideTextFontFamily((sh as { fontFamily?: unknown }).fontFamily),
        colorHex: normalizeHexColor((sh as { colorHex?: unknown }).colorHex),
        bold: !!(sh as { bold?: unknown }).bold,
        textAlign: normalizeTextAlign((sh as { textAlign?: unknown }).textAlign),
        zIndex: Number.isFinite(Number((sh as { zIndex?: unknown }).zIndex))
          ? Number((sh as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + shapes.length,
        ...(shapeGroupId ? { groupId: shapeGroupId } : {}),
        ...((sh as { locked?: unknown }).locked ? { locked: true } : {}),
        ...(shape === 'ruled'
          ? {
              ruledLineSpacingPx: Number.isFinite(ruledSp)
                ? Math.min(SLIDE_RULED_MAX_SPACING_PX, Math.max(SLIDE_RULED_MIN_SPACING_PX, Math.round(ruledSp)))
                : SLIDE_RULED_DEFAULT_SPACING_PX,
              ruledLineColorHex: typeof ruledColor === 'string' && /^#[0-9a-f]{3,8}$/i.test(ruledColor) ? ruledColor : '#c8c8c8',
              ruledLineDashed: !!(sh as { ruledLineDashed?: unknown }).ruledLineDashed,
              // 예전 'blank'(그리기) 값은 격자 선으로 이전
              ...((() => {
                const raw = (sh as { ruledBackground?: unknown }).ruledBackground;
                if (raw === 'dots') return { ruledBackground: 'dots' as const };
                if (raw === 'grid' || raw === 'blank') return { ruledBackground: 'grid' as const };
                return {};
              })()),
            }
          : {}),
      });
    }
  }
  const tables: ProgramSlideTableBlockV1[] = [];
  if (Array.isArray(o.tables)) {
    for (const tbl of o.tables) {
      if (!tbl || typeof tbl !== 'object') continue;
      const parsed = parseSlideTableBlock(tbl, boxes.length + images.length + buttons.length + shapes.length + tables.length);
      if (parsed) tables.push(parsed);
    }
  }
  const polylines: ProgramSlidePolylineBlockV1[] = [];
  if (Array.isArray(o.polylines)) {
    for (const pl of o.polylines) {
      if (!pl || typeof pl !== 'object') continue;
      const rawPts = (pl as { points?: unknown }).points;
      const points: Array<{ x: number; y: number }> = [];
      if (Array.isArray(rawPts)) {
        for (const p of rawPts) {
          if (!p || typeof p !== 'object') continue;
          const px = Number((p as { x?: unknown }).x);
          const py = Number((p as { y?: unknown }).y);
          if (Number.isFinite(px) && Number.isFinite(py)) {
            points.push({ x: clamp01(px), y: clamp01(py) });
          }
        }
      }
      const strokeW = Number((pl as { strokeWidthPx?: unknown }).strokeWidthPx);
      const plGroupId = parseSlideBlockGroupId((pl as { groupId?: unknown }).groupId);
      polylines.push({
        id: String((pl as { id?: unknown }).id || genSlideBoxId(polylines.length)),
        points,
        strokeColorHex: normalizeHexColor((pl as { strokeColorHex?: unknown }).strokeColorHex),
        strokeWidthPx: Number.isFinite(strokeW) ? Math.min(16, Math.max(1, Math.round(strokeW))) : 12,
        startArrow: !!(pl as { startArrow?: unknown }).startArrow,
        endArrow: !!(pl as { endArrow?: unknown }).endArrow,
        zIndex: Number.isFinite(Number((pl as { zIndex?: unknown }).zIndex))
          ? Number((pl as { zIndex?: unknown }).zIndex)
          : boxes.length + images.length + buttons.length + shapes.length + polylines.length,
        ...(plGroupId ? { groupId: plGroupId } : {}),
        ...((pl as { locked?: unknown }).locked ? { locked: true } : {}),
      });
    }
  }
  return {
    v: 1,
    appliedLayoutTemplateId: String(o.appliedLayoutTemplateId ?? '').trim() || undefined,
    defaultTextColorHex,
    defaultBackgroundColorHex,
    pageBackgroundColorHex,
    pageTextColorHex,
    boxes,
    images,
    clipboardImages,
    scanImages,
    buttons,
    shapes,
    polylines,
    tables,
    videos,
    webpages,
    quizzes,
    quizChoiceSets,
    quizChoiceOptions,
  };
}

function parseSlideImageBlockArray(
  raw: unknown,
  boxCount: number,
  priorImageCount: number
): ProgramSlideImageBlockV1[] {
  const images: ProgramSlideImageBlockV1[] = [];
  if (!Array.isArray(raw)) return images;
  for (const img of raw) {
    if (!img || typeof img !== 'object') continue;
    const x = (img as { x?: unknown }).x;
    const y = (img as { y?: unknown }).y;
    const w = (img as { w?: unknown }).w;
    const h = (img as { h?: unknown }).h;
    const naturalW = Number((img as { naturalW?: unknown }).naturalW);
    const naturalH = Number((img as { naturalH?: unknown }).naturalH);
    const rawMult = (img as { aspectWMultiplier?: unknown }).aspectWMultiplier;
    const rawFrameFrac = (img as { cropUsesFrameFrac?: unknown }).cropUsesFrameFrac;
    const rawCrop = (img as { crop?: unknown }).crop;
    const crop = rawCrop && typeof rawCrop === 'object' ? rawCrop : {};
    const imgGroupId = parseSlideBlockGroupId((img as { groupId?: unknown }).groupId);
    images.push({
      id: String((img as { id?: unknown }).id || genSlideBoxId(images.length)),
      x: typeof x === 'number' && Number.isFinite(x) ? clamp01(x) : 0.12,
      y: typeof y === 'number' && Number.isFinite(y) ? clamp01(y) : 0.12,
      w: typeof w === 'number' && Number.isFinite(w) ? clampDim(w) : 0.32,
      h: typeof h === 'number' && Number.isFinite(h) ? clampDim(h) : 0.24,
      src: normalizeImageSrc((img as { src?: unknown }).src),
      naturalW: Number.isFinite(naturalW) && naturalW > 0 ? naturalW : 0,
      naturalH: Number.isFinite(naturalH) && naturalH > 0 ? naturalH : 0,
      aspectWMultiplier: clampAspectWMultiplier(Number(rawMult)),
      cropUsesFrameFrac: rawFrameFrac === true,
      crop: normalizeImageCrop(crop),
      zIndex: Number.isFinite(Number((img as { zIndex?: unknown }).zIndex))
        ? Number((img as { zIndex?: unknown }).zIndex)
        : boxCount + priorImageCount + images.length,
      ...(imgGroupId ? { groupId: imgGroupId } : {}),
      ...((img as { locked?: unknown }).locked ? { locked: true } : {}),
    });
  }
  return images;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function clampDim(n: number): number {
  return Math.min(1, Math.max(0.05, n));
}

/** 도형 w/h 전용. `clampDim`(최소 0.05)을 쓰면 작은 원·직사각형이 복사·붙여넣기 후 크기가 바뀜 */
function clampShapeDim(n: number): number {
  if (!Number.isFinite(n)) return 0.14;
  return Math.min(1, Math.max(0.01, n));
}

function clampAspectWMultiplier(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.min(20, Math.max(0.05, Math.round(n * 10000) / 10000));
}

function clampFontSize(n: number): number {
  if (!Number.isFinite(n)) return 72;
  return Math.min(96, Math.max(8, Math.round(n)));
}

function clampCompactFontSize(n: number): number {
  if (!Number.isFinite(n)) return 32;
  return Math.min(96, Math.max(8, Math.round(n)));
}

function clampLineHeight(n: number): number {
  if (!Number.isFinite(n)) return 1.05;
  return Math.min(2.4, Math.max(1, Math.round(n * 100) / 100));
}

function clampCornerRadius(n: number): number {
  if (!Number.isFinite(n)) return 999;
  return Math.min(999, Math.max(0, Math.round(n)));
}

function genSlideBoxId(idx: number): string {
  try {
    const c = globalThis.crypto?.randomUUID?.();
    if (c) return c;
  } catch {
    /* ignore */
  }
  return `box-${Date.now()}-${idx}`;
}

function normalizeHexColor(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  return '#141414';
}

function normalizeShapeFillHex(raw: unknown): string {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === '' || s === 'transparent') return '';
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  return '#ffffff';
}

function normalizeQuizTextColorHex(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  return QUIZ_DEFAULT_TEXT_COLOR_HEX;
}

function normalizeButtonBackgroundHex(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  return '#ffffff';
}

function normalizeTextAlign(raw: unknown): 'justify' | 'left' | 'center' | 'right' {
  if (raw === 'justify' || raw === 'center' || raw === 'right') return raw;
  return 'left';
}

function normalizeImageSrc(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (s.startsWith('blob:')) return s;
  try {
    const url = new URL(s);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function normalizeButtonHref(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  try {
    const url = new URL(s);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return s;
  }
}

function normalizeVideoBookmarks(raw: unknown): ProgramSlideVideoBookmarkV1[] {
  if (!Array.isArray(raw)) return [];
  const out: ProgramSlideVideoBookmarkV1[] = [];
  for (const bm of raw) {
    if (!bm || typeof bm !== 'object') continue;
    const timeSec = Number((bm as { timeSec?: unknown }).timeSec);
    if (!Number.isFinite(timeSec) || timeSec < 0) continue;
    out.push({
      id: String((bm as { id?: unknown }).id || genSlideBoxId(out.length)),
      label: String((bm as { label?: unknown }).label || `북마크${out.length + 1}`),
      timeSec: Math.round(timeSec * 100) / 100,
    });
  }
  return out.sort((a, b) => a.timeSec - b.timeSec).slice(0, 20);
}

function normalizeImageCrop(raw: object): ProgramSlideImageCropV1 {
  const x = Number((raw as { x?: unknown }).x);
  const y = Number((raw as { y?: unknown }).y);
  const w = Number((raw as { w?: unknown }).w);
  const h = Number((raw as { h?: unknown }).h);
  const cropW = Number.isFinite(w) ? Math.min(1, Math.max(0.05, w)) : 1;
  const cropH = Number.isFinite(h) ? Math.min(1, Math.max(0.05, h)) : 1;
  return {
    x: Number.isFinite(x) ? Math.min(1 - cropW, Math.max(0, x)) : 0,
    y: Number.isFinite(y) ? Math.min(1 - cropH, Math.max(0, y)) : 0,
    w: cropW,
    h: cropH,
  };
}
